import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CreditCardIcon, BuildingIcon, SmartphoneIcon, InfoIcon } from 'lucide-react';
import { formatNaira } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface FundWalletProps {
  navigate: (screen: string) => void;
}
export function FundWallet({
  navigate
}: FundWalletProps) {
  const [amount, setAmount] = useState<string>('');
  const [method, setMethod] = useState<'card' | 'bank'>('card');
  const quickAmounts = [5000, 10000, 20000, 50000, 100000];
  const numAmount = amount ? parseInt(amount.replace(/,/g, '')) : 0;
  const fee = method === 'card' ? numAmount * 0.015 : 0;
  const total = numAmount + fee;
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val) {
      setAmount(parseInt(val).toLocaleString('en-US'));
    } else {
      setAmount('');
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 20
  }} className="flex-1 overflow-y-auto pb-safe bg-slate-50 flex flex-col h-full" data-id="element-1693">
      <TopBar title="Fund Wallet" showBack onBack={() => navigate('wallet')} data-id="element-1694" />

      <div className="px-5 pt-4 pb-6 flex-1 flex flex-col" data-id="element-1695">
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex flex-col items-center justify-center mb-6" data-id="element-1696">
          <motion.img src="https://cdn.magicpatterns.com/uploads/ai3GT46WXxVymwerHg44X4/default_mascott_-_green.png" alt="Mascot" className="w-16 h-16 object-contain mb-2" animate={{
          y: [0, -6, 0]
        }} transition={{
          repeat: Infinity,
          duration: 2.5,
          ease: 'easeInOut'
        }} data-id="element-1697" />
          <p className="text-sm font-medium text-gray-500 text-center" data-id="element-1698">
            Top up your wallet to stay protected.
          </p>
        </motion.div>

        {/* Payment Methods */}
        <div className="mb-8" data-id="element-1699">
          <h3 className="text-sm font-bold text-[#0D3B3B] mb-3 px-1" data-id="element-1700">
            Select Funding Method
          </h3>
          <div className="space-y-3" data-id="element-1701">
            <div onClick={() => setMethod('card')} className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-4 ${method === 'card' ? 'border-amber-500 bg-amber-50/30 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-1702">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${method === 'card' ? 'bg-amber-100 text-amber-600' : 'bg-gray-50 text-gray-500'}`} data-id="element-1703">
                <CreditCardIcon className="w-5 h-5" data-id="element-1704" />
              </div>
              <div className="flex-1" data-id="element-1705">
                <h4 className="font-bold text-[#0D3B3B] text-sm" data-id="element-1706">
                  Card Payment
                </h4>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-1707">
                  Paystack / Flutterwave • 1.5% fee
                </p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${method === 'card' ? 'border-amber-500' : 'border-gray-300'}`} data-id="element-1708">
                {method === 'card' && <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" data-id="element-1709"></div>}
              </div>
            </div>

            <div onClick={() => setMethod('bank')} className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-4 ${method === 'bank' ? 'border-amber-500 bg-amber-50/30 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-1710">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${method === 'bank' ? 'bg-amber-100 text-amber-600' : 'bg-gray-50 text-gray-500'}`} data-id="element-1711">
                <BuildingIcon className="w-5 h-5" data-id="element-1712" />
              </div>
              <div className="flex-1" data-id="element-1713">
                <h4 className="font-bold text-[#0D3B3B] text-sm" data-id="element-1714">
                  Direct Bank Transfer
                </h4>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-1715">
                  Embedly NIP Inflow • Free
                </p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${method === 'bank' ? 'border-amber-500' : 'border-gray-300'}`} data-id="element-1716">
                {method === 'bank' && <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" data-id="element-1717"></div>}
              </div>
            </div>
          </div>
        </div>

        {method === 'card' ? <>
            {/* Amount Input */}
            <div className="text-center mb-8" data-id="element-1718">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-1719">
                Enter Amount
              </p>
              <div className="flex items-center justify-center text-5xl font-bold text-[#0D3B3B] mb-6" data-id="element-1720">
                <span className="text-gray-400 mr-1" data-id="element-1721">₦</span>
                <input type="text" value={amount} onChange={handleAmountChange} placeholder="0" className="bg-transparent w-full max-w-[200px] text-center focus:outline-none placeholder-gray-300" autoFocus data-id="element-1722" />
              </div>

              {/* Quick Amounts */}
              <div className="flex flex-wrap justify-center gap-2" data-id="element-1723">
                {quickAmounts.map(amt => <button key={amt} onClick={() => setAmount(amt.toLocaleString('en-US'))} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-bold text-[#0D3B3B] shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-all active:scale-95" data-id="element-1724">
                    {formatNaira(amt)}
                  </button>)}
              </div>
            </div>

            <div className="mt-auto" data-id="element-1725">
              {/* Summary */}
              {numAmount > 0 && <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm mb-6" data-id="element-1726">
                  <div className="flex justify-between text-sm mb-2" data-id="element-1727">
                    <span className="text-gray-500 font-medium" data-id="element-1728">Amount</span>
                    <span className="font-bold text-[#0D3B3B]" data-id="element-1729">
                      {formatNaira(numAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mb-3 pb-3 border-b border-gray-50" data-id="element-1730">
                    <span className="text-gray-500 font-medium flex items-center gap-1" data-id="element-1731">
                      Fee <InfoIcon className="w-3 h-3" data-id="element-1732" />
                    </span>
                    <span className="font-bold text-[#0D3B3B]" data-id="element-1733">
                      {fee > 0 ? formatNaira(fee) : 'Free'}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1734">
                    <span className="font-bold text-[#0D3B3B]" data-id="element-1735">
                      Total to Pay
                    </span>
                    <span className="font-bold text-lg text-amber-600" data-id="element-1736">
                      {formatNaira(total)}
                    </span>
                  </div>
                </div>}

              <button disabled={numAmount === 0} onClick={() => navigate('wallet')} className={`w-full py-4 rounded-xl font-bold text-lg transition-all active:scale-95 ${numAmount > 0 ? 'bg-[#E85D3A] hover:bg-[#D04E2E] text-white shadow-lg shadow-amber-500/30' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-1737">
                Fund Wallet
              </button>
            </div>
          </> : <div className="mt-4" data-id="element-1738">
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm mb-6" data-id="element-1739">
              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-50" data-id="element-1740">
                <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center" data-id="element-1741">
                  <BuildingIcon className="w-6 h-6 text-emerald-600" data-id="element-1742" />
                </div>
                <div data-id="element-1743">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-1744">
                    Bank Name
                  </p>
                  <p className="font-bold text-[#0D3B3B] text-lg" data-id="element-1745">
                    Embedly Bank
                  </p>
                </div>
              </div>

              <div className="mb-6" data-id="element-1746">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-1747">
                  Account Number
                </p>
                <div className="flex items-center justify-between" data-id="element-1748">
                  <p className="font-black text-[#0D3B3B] text-3xl tracking-wider" data-id="element-1749">
                    9901234567
                  </p>
                  <button className="text-amber-600 text-sm font-bold bg-amber-50 px-3 py-1.5 rounded-lg hover:bg-amber-100 transition-colors" data-id="element-1750">
                    Copy
                  </button>
                </div>
              </div>

              <div data-id="element-1751">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-1752">
                  Account Name
                </p>
                <p className="font-bold text-[#0D3B3B] text-lg" data-id="element-1753">
                  Skydd - Adaeze Okonkwo
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3 mb-8" data-id="element-1754">
              <InfoIcon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" data-id="element-1755" />
              <p className="text-sm text-blue-800 leading-relaxed" data-id="element-1756">
                Transfer funds directly to this account from any Nigerian bank.
                Your Skydd wallet will be credited instantly once the transfer
                is successful.
              </p>
            </div>

            <button onClick={() => navigate('wallet')} className="w-full py-4 rounded-xl font-bold text-lg transition-all active:scale-95 bg-gray-100 text-[#0D3B3B] hover:bg-gray-200" data-id="element-1757">
              Back to Wallet
            </button>
          </div>}
      </div>
    </motion.div>;
}